import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { AlertContext, AlertRule, BudgetAlert } from '../models/alert-framework.model';

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  private rules = signal<AlertRule[]>([]);
  private dismissedAlertIds = signal<Set<string>>(this.loadDismissedAlerts());

  /**
   * Action bus when user clicks an alert button (e.g. 'RUN_AUTO_BALANCE')
   */
  readonly actionTriggered$ = new Subject<{ alertId: string; actionKey: string }>();

  registerRule(rule: AlertRule): void {
    this.rules.update((existing) => {
      const filtered = existing.filter((r) => r.id !== rule.id);
      return [...filtered, rule];
    });
  }

  evaluateAlerts(ctx: AlertContext): BudgetAlert[] {
    const dismissed = this.dismissedAlertIds();
    const activeAlerts: BudgetAlert[] = [];

    for (const rule of this.rules()) {
      try {
        const alert = rule.evaluate(ctx);
        if (alert && !dismissed.has(alert.id)) {
          activeAlerts.push(alert);
        }
      } catch (err) {
        console.error(`Error evaluating rule ${rule.id}:`, err);
      }
    }

    return activeAlerts.sort(
      (a, b) => this.severityWeight(b.severity) - this.severityWeight(a.severity),
    );
  }

  dismissAlert(alertId: string): void {
    this.dismissedAlertIds.update((set) => {
      const next = new Set(set);
      next.add(alertId);
      this.persistDismissed(next);
      return next;
    });
  }

  triggerAction(alertId: string, actionKey: string): void {
    this.actionTriggered$.next({ alertId, actionKey });
  }

  private severityWeight(s: string): number {
    switch (s) {
      case 'danger':
        return 3;
      case 'warning':
        return 2;
      case 'info':
        return 1;
      default:
        return 0;
    }
  }

  private loadDismissedAlerts(): Set<string> {
    try {
      const stored = localStorage.getItem('slt_dismissed_alerts');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  }

  private persistDismissed(set: Set<string>): void {
    localStorage.setItem('slt_dismissed_alerts', JSON.stringify(Array.from(set)));
  }
}
